import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import { Avatar, Box, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import IconWrapper from "../../common/components/functional/icons/IconWrapper";
import { ReactComponent as MultiChannelUserMessageIcon } from "../../common/icons/multi-channel-user-message.svg";
import {
  getUserMessageChannelLabelKey,
  normalizeUserMessageChannels,
} from "./userMessagesHelpers";
import { UserMessageChannel } from "./userMessagesTypes";

interface Props {
  channels?: readonly string[] | null;
  framed?: boolean;
}

const iconSx = { fontSize: 18 };

const ChannelIcon = ({
  channel,
  framed,
}: {
  channel: UserMessageChannel;
  framed?: boolean;
}) => {
  const { t } = useTranslation("userMessages");
  const label = channel === "Email" ? t("channelEmail") : t("channelInApp");
  const icon =
    channel === "Email" ? (
      <EmailOutlinedIcon sx={iconSx} />
    ) : (
      <ArticleOutlinedIcon sx={iconSx} />
    );

  return (
    <Tooltip title={label}>
      {framed ? (
        <Avatar sx={{ width: 32, height: 32 }} aria-label={label}>
          {icon}
        </Avatar>
      ) : (
        <Box component="span" display="inline-flex" aria-label={label}>
          {icon}
        </Box>
      )}
    </Tooltip>
  );
};

const multiChannelIconSx = (color: string) => ({
  fontSize: 18,
  color,
  fill: "none",
  "& path, & rect": { fill: "none", stroke: "currentColor" },
});

const MultiChannelIcon = ({
  label,
  framed,
}: {
  label: string;
  framed: boolean;
}) => {
  const icon = (
    <IconWrapper
      icon={MultiChannelUserMessageIcon}
      sx={multiChannelIconSx(framed ? "#fff" : "currentColor")}
    />
  );

  return (
    <Tooltip title={label}>
      {framed ? (
        <Avatar sx={{ width: 32, height: 32 }} aria-label={label}>
          {icon}
        </Avatar>
      ) : (
        <Box component="span" display="inline-flex" aria-label={label}>
          {icon}
        </Box>
      )}
    </Tooltip>
  );
};

const UserMessageChannelIcons = ({ channels, framed = false }: Props) => {
  const { t } = useTranslation("userMessages");
  const normalized = normalizeUserMessageChannels(channels);
  const label = t(getUserMessageChannelLabelKey(normalized));

  if (normalized.length > 1) {
    return <MultiChannelIcon label={label} framed={framed} />;
  }

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={framed ? 0.5 : "2px"}
      aria-label={label}
    >
      {normalized.map((channel) => (
        <ChannelIcon key={channel} channel={channel} framed={framed} />
      ))}
    </Box>
  );
};

export default UserMessageChannelIcons;
